import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { loadStandalonePage } from "~/utils/content-fns";
import { loadAshbyJobs, type AshbyJob } from "~/utils/ashby";
import { seo, seoLinks } from "~/utils/seo";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "~/components/Button";

/** Ashby's own hosted board — last-resort fallback if the API fetch fails. */
const ASHBY_BOARD_URL = "https://jobs.ashbyhq.com/C1";

function WhyCarousel({ images }: { images: string[] }) {
  const [emblaRef] = useEmblaCarousel(
    { loop: true, align: "start", dragFree: true },
    [Autoplay({ delay: 1800, stopOnInteraction: false })],
  );

  return (
    <div className="relative mt-20 overflow-hidden md:mt-12 lg:mt-28">
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-3">
          {images.map((img, i) => (
            <div key={i} className="min-w-0 flex-[0_0_auto]">
              <img
                src={img}
                alt=""
                className="max-h-[18.5rem] w-auto rounded-20"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/careers/")({
  loader: async () => {
    const [data, jobs] = await Promise.all([
      loadStandalonePage({ data: { path: "careers/_index.md" } }),
      loadAshbyJobs(),
    ]);
    return {
      title: (data?.title as string) || "Careers",
      seo_title: (data?.seo_title as string) || "Careers - C1",
      seo_description: data?.seo_description as string | undefined,
      hero: data?.hero as any,
      stats: data?.stats as any,
      why: data?.why as any,
      locations: data?.locations as any,
      benefits: data?.benefits as any,
      join: data?.join as any,
      jobs,
    };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title,
          description: loaderData.seo_description,
          url: "/careers",
        })
      : [],
    links: seoLinks({ url: "/careers" }),
  }),
  component: Careers,
});

const ALL = "";

/** Distinct, sorted values for a filter dropdown's options. */
function optionsFor(jobs: AshbyJob[], key: keyof AshbyJob): string[] {
  const values = jobs.map((j) => j[key]).filter((v): v is string => typeof v === "string" && v.length > 0);
  return Array.from(new Set(values)).sort();
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="mb-1 block font-body text-13 font-semibold uppercase tracking-widest-4 text-gray-60">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-[#DCDADA] bg-white px-3 py-2 font-body text-charcoal focus:border-teal-dark focus:outline-none focus:ring-1 focus:ring-teal-dark"
      >
        <option value={ALL}>All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Crawlable, internally-linked list of open roles, grouped by department,
 * replacing the old Ashby iframe embed — that rendered Ashby's own UI with
 * job links pointing off our domain, so nothing was deep-linkable or
 * indexable on c1.ai. Falls back to Ashby's hosted board directly if the
 * API fetch failed (see `fetchAshbyJobs`), so a transient outage doesn't
 * leave visitors with no way to see open roles at all.
 *
 * Filters default to "All" so the unfiltered list — every job, every link —
 * is what's actually in the server-rendered HTML; the dropdowns are
 * client-side progressive enhancement on top of that, not a gate on it.
 */
function OpenRoles({ jobs }: { jobs: AshbyJob[] }) {
  const [department, setDepartment] = useState(ALL);
  const [location, setLocation] = useState(ALL);
  const [employmentType, setEmploymentType] = useState(ALL);
  const [workplaceType, setWorkplaceType] = useState(ALL);

  const departments = useMemo(() => optionsFor(jobs, "department"), [jobs]);
  const locations = useMemo(
    () => Array.from(new Set(jobs.flatMap((j) => j.locations.map((l) => l.name)))).sort(),
    [jobs],
  );
  const employmentTypes = useMemo(() => optionsFor(jobs, "employmentType"), [jobs]);
  const workplaceTypes = useMemo(() => optionsFor(jobs, "workplaceType"), [jobs]);

  if (jobs.length === 0) {
    return (
      <p className="mt-8 font-body text-gray-80">
        Our open roles are temporarily unavailable here — see them on{" "}
        <a href={ASHBY_BOARD_URL} className="font-semibold text-teal-dark hover:underline">
          our job board
        </a>
        .
      </p>
    );
  }

  const filtered = jobs.filter(
    (job) =>
      (department === ALL || job.department === department) &&
      (location === ALL || job.locations.some((l) => l.name === location)) &&
      (employmentType === ALL || job.employmentType === employmentType) &&
      (workplaceType === ALL || job.workplaceType === workplaceType),
  );

  const byDepartment = new Map<string, AshbyJob[]>();
  for (const job of filtered) {
    const dept = job.department || "Other";
    if (!byDepartment.has(dept)) byDepartment.set(dept, []);
    byDepartment.get(dept)!.push(job);
  }

  return (
    <div className="mt-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FilterSelect label="Department" value={department} onChange={setDepartment} options={departments} />
        <FilterSelect label="Location" value={location} onChange={setLocation} options={locations} />
        <FilterSelect
          label="Employment type"
          value={employmentType}
          onChange={setEmploymentType}
          options={employmentTypes}
        />
        <FilterSelect
          label="Location type"
          value={workplaceType}
          onChange={setWorkplaceType}
          options={workplaceTypes}
        />
      </div>

      {filtered.length === 0 ? (
        <p className="mt-8 font-body text-gray-80">No open roles match those filters.</p>
      ) : (
        <div className="mt-8 space-y-10">
          {Array.from(byDepartment.entries()).map(([dept, deptJobs]) => (
            <div key={dept}>
              <h3 className="font-body text-13 font-semibold uppercase tracking-widest-4 text-gray-60">
                {dept}
              </h3>
              <ul className="mt-3 divide-y divide-[#DCDADA] rounded-lg border border-[#DCDADA] bg-white">
                {deptJobs.map((job) => (
                  <li key={job.id}>
                    <Link
                      to="/careers/$jobId"
                      params={{ jobId: job.id }}
                      className="group flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-[#EBEBE9]"
                    >
                      <span className="min-w-0">
                        <span className="block font-body font-semibold text-charcoal group-hover:text-teal-dark">
                          {job.title}
                        </span>
                        {job.salarySummary && (
                          <span className="block font-body text-13 text-gray-60">
                            {job.salarySummary}
                          </span>
                        )}
                      </span>
                      <span className="flex max-w-[50%] shrink-0 flex-wrap justify-end gap-x-3 text-right font-body text-13 text-gray-60">
                        {job.locations.length > 0 && (
                          <span>
                            {job.locations.map((l, i) => (
                              <Fragment key={l.name}>
                                {i > 0 && ", "}
                                <span className="whitespace-nowrap">{l.name}</span>
                              </Fragment>
                            ))}
                          </span>
                        )}
                        {job.workplaceType && <span>{job.workplaceType}</span>}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Careers() {
  const { hero, stats, why, locations, benefits, join, jobs } = Route.useLoaderData();

  return (
    <div>
      {/* Hero */}
      {hero && (
        <div className="relative overflow-hidden px-4 py-10 sm:py-16 md:px-8 xl:pb-28">
          <div className="relative mx-auto max-w-screen-xl">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-0">
              <div className="lg:w-1/2 2xl:w-5/12">
                {hero.label && (
                  <div className="mb-5 flex xl:mb-7">
                    <span className="chip bg-pink-accent text-charcoal">
                      {hero.label}
                    </span>
                  </div>
                )}
                {hero.heading && (
                  <h1 className="font-display text-33 font-bold text-charcoal md:text-5xl lg:text-57">
                    {hero.heading}
                  </h1>
                )}
                {hero.copy && (
                  <p className="mt-3 font-body leading-relaxed text-gray-80 md:text-19 lg:pr-16 xl:mt-5">
                    {hero.copy}
                  </p>
                )}
                {hero.button && (
                  <div className="mt-6 flex md:mt-8">
                    <Button href={hero.button.url} variant="teal">
                      {hero.button.text}
                    </Button>
                  </div>
                )}
              </div>
              {hero.image && (
                <div className="md:-mr-14 md:flex-1">
                  <img src={hero.image} alt={hero.heading} loading="lazy" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats?.cards && (
        <div className="px-4 md:px-8">
          <div className="mx-auto max-w-screen-xl border border-[#DCDADA] bg-[#EBEBE9]">
            <div className="flex flex-wrap justify-center divide-x divide-[#DCDADA] py-7 lg:py-12">
              {(stats.cards as any[]).map((stat: any, i: number) => (
                <div
                  key={i}
                  className="w-1/2 pb-7 text-center text-charcoal md:w-1/3 lg:pb-0 px-6"
                >
                  {stat.heading && (
                    <h2 className="font-display text-33 font-bold leading-tight">
                      {stat.heading}
                    </h2>
                  )}
                  {stat.copy && (
                    <p
                      className="font-body text-19 font-medium text-gray-80 lg:mt-0.5"
                      dangerouslySetInnerHTML={{ __html: stat.copy }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Why */}
      {why && (
        <div className="relative overflow-hidden pb-16 pt-14 md:py-20 lg:py-24">
          <div className="relative mx-auto max-w-screen-xl px-4 md:px-8">
            <div className="grid gap-8 md:gap-16 lg:grid-cols-2 lg:items-center">
              <div>
                {why.heading && (
                  <h2 className="font-display text-57 font-bold text-charcoal">
                    {why.heading}
                  </h2>
                )}
                {why.copy && (
                  <div
                    className="mt-5 space-y-5 font-body leading-relaxed text-gray-80"
                    dangerouslySetInnerHTML={{
                      __html: (why.copy as string)
                        .replace(/\n\n/g, "</p><p>")
                        .replace(/^/, "<p>")
                        .replace(/$/, "</p>"),
                    }}
                  />
                )}
                {why.copy_2 && (
                  <p
                    className="mt-7 font-body font-bold text-charcoal"
                    dangerouslySetInnerHTML={{ __html: why.copy_2 }}
                  />
                )}
              </div>
              <div>
                {why.image && (
                  <img
                    src={why.image}
                    alt={why.heading}
                    className="rounded-2xl"
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          </div>
          {/* Image carousel (embla, replacing Splide) */}
          {why.images && <WhyCarousel images={why.images as string[]} />}
        </div>
      )}

      {/* Locations */}
      {locations?.cards && (
        <div className="relative overflow-hidden px-4 py-14 md:px-8 md:py-20 lg:py-24">
          <div className="relative mx-auto max-w-screen-xl">
            <div className="relative flex flex-wrap overflow-hidden rounded-20 bg-teal-dark px-6 py-9 lg:items-center lg:px-8 lg:py-14">
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
              <div className="relative flex w-full items-center md:w-2/5 lg:w-[30%] xl:w-[36%]">
                {locations.heading && (
                  <h2 className="mb-7 font-display text-33 font-bold leading-none text-white md:mb-0 md:max-w-[13rem] lg:text-[2.5rem] xl:max-w-none">
                    {locations.heading}
                  </h2>
                )}
              </div>
              {(locations.cards as any[]).map((loc: any, i: number) => (
                <div
                  key={i}
                  className="relative mt-6 w-1/2 pr-10 md:mt-0 md:w-[30%] lg:w-[30%]"
                >
                  <svg className="h-5 w-5 lg:h-6 lg:w-6" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M8.668 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm6-1.8c0-3.63-2.65-6.2-6-6.2s-6 2.57-6 6.2c0 2.34 1.95 5.44 6 9.14 4.05-3.7 6-6.8 6-9.14ZM8.668 0c4.2 0 8 3.22 8 8.2 0 3.32-2.67 7.25-8 11.8-5.33-4.55-8-8.48-8-11.8C.668 3.22 4.468 0 8.668 0Z"
                      fill="#ff92eb"
                    />
                  </svg>
                  {loc.heading && (
                    <h3 className="mt-2.5 font-body text-11 font-semibold uppercase tracking-widest-4 text-white/70">
                      {loc.heading}
                    </h3>
                  )}
                  {loc.copy && (
                    <p
                      className="mt-2 font-body font-medium leading-snug text-gray-30"
                      dangerouslySetInnerHTML={{ __html: loc.copy }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Benefits */}
      {benefits?.cards && (
        <div className="relative overflow-hidden px-4 pb-20 pt-16 md:px-8 lg:py-24">
          <div className="relative mx-auto max-w-[18rem] md:max-w-xl xl:max-w-screen-xl">
            {benefits.heading && (
              <h2 className="font-display text-5xl font-bold text-charcoal">
                {benefits.heading}
              </h2>
            )}
            <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {(benefits.cards as any[]).map((card: any, i: number) => (
                <div
                  key={i}
                  className="rounded-[14px] border border-[#DCDADA] bg-white px-5 pb-16 pt-6 text-center"
                >
                  {card.icon && (
                    <div className="flex justify-center">
                      <img
                        src={`/images/svg/${card.icon}`}
                        alt=""
                        className="h-20 w-20"
                      />
                    </div>
                  )}
                  {card.heading && (
                    <h3 className="mt-6 font-body text-19 font-bold text-charcoal">
                      {card.heading}
                    </h3>
                  )}
                  {card.copy && (
                    <p className="mt-2 font-body leading-relaxed text-gray-80">
                      {card.copy}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Join — open roles */}
      {join && (
        <div id="join" className="px-4 py-16 md:px-8 md:py-28 lg:pt-32">
          <div className="mx-auto max-w-screen-xl">
            {join.heading && (
              <h2 className="font-display text-[2.5rem] font-bold text-charcoal lg:text-5xl">
                {join.heading}
              </h2>
            )}
            <OpenRoles jobs={jobs} />
          </div>
        </div>
      )}
    </div>
  );
}
